/**
 * The missile cam (WORKBOARD I9's other half; decision 88: plain): the turn game's shot seen from the
 * round — the camera rides just behind the projectile, looking along its flight, in the same feed
 * frame as the drone feed (ui/feedFrame.ts): speed, altitude over the nearest world, flight time, the
 * seconds to the predicted impact, the weapon; the predicted impact boxed ("IMPACT") as the shot
 * closes on it. At the end the feed says IMPACT (or LOST) for a moment and the view hands back.
 *
 * It is the third shot-camera setting (Settings → Shot camera: connect / follow / ride), presentation
 * only: the orbit camera keeps following the shot underneath, so the hand-back is where the impact
 * is. A pinned view (right-click / Tab) wins over it, as over the connecting cam. The view maths is
 * pure and exported (tests/missileCam.test.ts).
 */
import * as THREE from 'three';
import type { PlanetState, ProjectileState, Vec3 } from '../types';
import { getSurfaceRadius } from '../core/surface';
import { WEAPONS } from '../constants';
import { FeedFrame } from './feedFrame';

/** The ride's field of view; how far behind the shot the camera sits (× the shot's own size) and above it. */
export const RIDE_FOV = 62;
export const RIDE_BACK = 1.6;
export const RIDE_LIFT = 0.45;
/** How long IMPACT / LOST stays up before the view hands back (seconds). */
export const RIDE_END_SECONDS = 1.1;

const norm = (v: Vec3): Vec3 => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };

/** The nearest live world to a point, and the point's height above its ground. */
export function nearestWorld(pos: Vec3, planets: readonly PlanetState[]): { planet: PlanetState | null; altitude: number } {
  let best: PlanetState | null = null, gap = Infinity;
  for (const p of planets) {
    if (p.type === 'wormhole' || p.alive === false) continue;
    const g = Math.hypot(pos.x - p.position.x, pos.y - p.position.y, pos.z - p.position.z) - p.radius;
    if (g < gap) { gap = g; best = p; }
  }
  if (!best) return { planet: null, altitude: Infinity };
  const rel = { x: pos.x - best.position.x, y: pos.y - best.position.y, z: pos.z - best.position.z };
  const ground = best.type === 'planet' ? getSurfaceRadius(best, norm(rel)) : best.radius;
  return { planet: best, altitude: Math.hypot(rel.x, rel.y, rel.z) - ground };
}

/**
 * Where the ride's camera is and what it looks at: RIDE_BACK behind the shot along its flight and
 * RIDE_LIFT above it (above = away from the nearest world, so the horizon reads), looking along the
 * flight — at the predicted impact once the shot is within a few seconds of it, so the target slides
 * into the reticle. World coordinates.
 */
export function rideView(projectile: Pick<ProjectileState, 'pos' | 'vel'>, planets: readonly PlanetState[], impact: Vec3 | null): { eye: Vec3; target: Vec3; up: Vec3 } {
  const v = norm(projectile.vel);
  const { planet } = nearestWorld(projectile.pos, planets);
  const up = planet ? norm({ x: projectile.pos.x - planet.position.x, y: projectile.pos.y - planet.position.y, z: projectile.pos.z - planet.position.z }) : { x: 0, y: 1, z: 0 };
  const eye = { x: projectile.pos.x - v.x * RIDE_BACK + up.x * RIDE_LIFT, y: projectile.pos.y - v.y * RIDE_BACK + up.y * RIDE_LIFT, z: projectile.pos.z - v.z * RIDE_BACK + up.z * RIDE_LIFT };
  const ahead = { x: projectile.pos.x + v.x * 8, y: projectile.pos.y + v.y * 8, z: projectile.pos.z + v.z * 8 };
  const target = impact && Math.hypot(impact.x - projectile.pos.x, impact.y - projectile.pos.y, impact.z - projectile.pos.z) < 12 ? impact : ahead;
  return { eye, target, up };
}

/** The readouts: speed (u/s), height over the nearest world (u), flight time (s), seconds to the predicted impact (straight-line) or null. */
export function rideReadouts(projectile: Pick<ProjectileState, 'pos' | 'vel' | 'time' | 'weaponKey'>, planets: readonly PlanetState[], impact: Vec3 | null): { speed: number; altitude: number; time: number; eta: number | null; weapon: string } {
  const speed = Math.hypot(projectile.vel.x, projectile.vel.y, projectile.vel.z);
  const { altitude } = nearestWorld(projectile.pos, planets);
  const eta = impact && speed > 1e-6 ? Math.hypot(impact.x - projectile.pos.x, impact.y - projectile.pos.y, impact.z - projectile.pos.z) / speed : null;
  return { speed, altitude, time: projectile.time, eta, weapon: WEAPONS[projectile.weaponKey]?.name ?? projectile.weaponKey };
}

export class MissileCam {
  private readonly frame: FeedFrame;
  private riding = false;
  private endAt: number | null = null;
  private time = 0;
  private readonly last = { eye: new THREE.Vector3(), target: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0) };

  constructor(canvas: HTMLCanvasElement) { this.frame = new FeedFrame(canvas, 'missile-cam'); }

  get active(): boolean { return this.riding || this.endAt !== null; }

  /**
   * Each frame, after the orbit camera has been placed: with a shot in flight the camera rides it;
   * when the shot is gone the feed says how it ended for a moment, the view frozen where it was, then
   * hands back. `impact` is the predicted impact point (or null); `ended` says what became of the
   * last shot. Returns whether the ride holds the camera this frame.
   */
  update(projectile: ProjectileState | null, planets: readonly PlanetState[], impact: Vec3 | null, ended: 'impact' | 'lost' | null, camera: THREE.PerspectiveCamera, dt: number): boolean {
    this.time += dt;
    if (projectile) {
      this.riding = true; this.endAt = null;
      this.frame.show();
      const v = rideView(projectile, planets, impact);
      this.last.eye.set(v.eye.x, v.eye.y, v.eye.z); this.last.target.set(v.target.x, v.target.y, v.target.z); this.last.up.set(v.up.x, v.up.y, v.up.z);
      const r = rideReadouts(projectile, planets, impact);
      this.frame.readout('tl', `<span class="live"></span>${r.weapon.toUpperCase()} · LIVE`);
      this.frame.readout('tr', `T+ ${r.time.toFixed(1)} S${r.eta !== null ? ` · IMPACT ${r.eta.toFixed(1)} S` : ''}`);
      this.frame.readout('bl', `SPD ${r.speed.toFixed(1)} · ALT ${Number.isFinite(r.altitude) ? r.altitude.toFixed(1) : '—'}`);
      this.frame.readout('br', impact ? 'TARGET LOCKED' : 'NO PREDICTION');
    } else if (this.riding) {
      this.riding = false; this.endAt = this.time;
      this.frame.message(ended === 'lost' ? 'SIGNAL LOST' : 'IMPACT');
    } else if (this.endAt !== null && this.time - this.endAt > RIDE_END_SECONDS) {
      this.endAt = null; this.frame.hide();
      return false;
    } else if (this.endAt === null) return false;
    // place the camera (the frozen last view while the end message shows)
    camera.position.copy(this.last.eye);
    camera.up.copy(this.last.up);
    camera.lookAt(this.last.target);
    if (camera.fov !== RIDE_FOV) { camera.fov = RIDE_FOV; camera.updateProjectionMatrix(); }
    camera.updateMatrixWorld(true);
    this.frame.beginMarks();
    if (projectile && impact) this.frame.box(camera, impact, 2.5, 'IMPACT', 0.9);
    return true;
  }

  /** Drop the ride at once (a new turn, a pinned view, the setting changed). */
  release(): void { this.riding = false; this.endAt = null; this.frame.hide(); }

  dispose(): void { this.release(); this.frame.dispose(); }
}
